import React from 'react';
import PropTypes from 'prop-types';
import ExternalLink from './ExternalLink';
import { graphql, useStaticQuery } from 'gatsby';
import { css } from '@emotion/react';
import useThemeTranslation from '../hooks/useThemeTranslation';
import Trans from './Trans';
import Link from './Link';
import RecaptchaFooter from './SignupModal/RecaptchaFooter';
import Button from './Button';

// We need to use this as a JS value otherwise the HTML entity gets saved in the
// string and escaped by React, therefore rendering the literal &copy; text in
// the footer
const copyrightSymbol = String.fromCharCode(169);
const year = new Date().getFullYear();

const GlobalFooter = ({ className }) => {
  const { t } = useThemeTranslation();
  const { sitePage } = useStaticQuery(graphql`
    query FooterQuery {
      sitePage(path: { eq: "/terms" }) {
        id
      }
    }
  `);

  const hasOsano = () => {
    if (typeof window !== 'undefined') {
      return window.Osano !== undefined;
    }
    return false;
  };

  const handlePrivacyClick = () => {
    window.Osano.cm.showDrawer('osano-cm-dom-info-dialog-open');
  };

    return (
        <footer
            data-swiftype-index={false}
            className={className}
            css={css`
                color: var(--system-text-primary-dark);
                background-color: #15273c;
                z-index: 1;

                a {
                    color: var(--system-text-primary-dark);
                    border-color: var(--system-text-primary-dark);
                }
            `}
        >
            <div>
                <div
                    css={css`
                        font-size: 0.8125rem;
                        font-weight: 400;
                        color: #fff;
                        text-transform: uppercase;
                        line-height: 1.25rem;
                        padding: 0.5rem var(--site-content-padding);
                        max-width: var(--site-max-width);
                        margin: 0 auto;

                        @media screen and (max-width: 760px) {
                            justify-content: center;
                            text-align: left;
                            grid-template-columns: auto;
                            grid-gap: 0.5rem;
                            grid-template-areas:
                'legal'
                'copyright';
                        }                      
                    `}
                >
                    <div
                        css={css`
                            display: flex;
                            flex-direction: row;
                            text-transform: uppercase;
                            
                            a:hover {
                                text-decoration: underline
                            }

                            @media screen and (max-width: 1000px) {
                                flex-direction: column;
                            }

                        `}
                    >
                        <div
                            css={css`
                                float: left;
                                width: 33.33%;
                                padding: 10px;
                                a {
                                    margin-left: 0.75rem;
                                    white-space: nowrap;
                                    text-decoration: none;
                                }
                            `}
                        >
                            <p>Paygate</p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <a href="https://developer.computop.com/en/">Documentation (EN)</a>
                            </p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <a href="https://developer.computop.com/de/">Dokumentation (DE)</a>
                            </p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <ExternalLink href="https://computop.statuspage.io/">Paygate Status</ExternalLink>
                            </p>
                        </div>

                        <div
                            css={css`
                                float: left;
                                width: 33.33%;
                                padding: 10px;
                                a {
                                    margin-left: 0.75rem;
                                    white-space: nowrap;
                                    text-decoration: none;
                                }
                            `}
                        >
                            <p>About&nbsp;Computop</p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <ExternalLink href="https://computop.com/">
                                    Company Website
                                </ExternalLink>
                            </p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <ExternalLink href="https://computop.com/payment-insights/">
                                    Payment Insights Blog
                                </ExternalLink>
                            </p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <ExternalLink href="https://www.youtube.com/user/ComputopTV">
                                    Youtube Channel
                                </ExternalLink>
                            </p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <ExternalLink href="https://www.kununu.com/de/computop-paygate">
                                    Kununu Profile
                                </ExternalLink>
                            </p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <ExternalLink href="https://www.linkedin.com/company/computop/">
                                    LinkedIn Profile
                                </ExternalLink>
                            </p>
                        </div>

                        <div
                            css={css`
                                float: left;
                                width: 33.33%;
                                padding: 10px;
                                a {
                                    margin-left: 0.75rem;
                                    white-space: nowrap;
                                    text-decoration: none;
                                }
                            `}
                        >
                            <p>Legal</p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <ExternalLink href="https://computop.com/contact/">
                                    Contact / Kontakt
                                </ExternalLink>
                            </p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <a href="https://developer.computop.com/en/disclaimer/">
                                    Disclaimer / Haftungsausschluss
                                </a>
                            </p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <ExternalLink href="https://computop.com/imprint">
                                    Imprint / Impressum
                                </ExternalLink>
                            </p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <ExternalLink href="https://computop.com/data-privacy">
                                    Data Privacy / Datenschutz
                                </ExternalLink>
                            </p>
                            <p css={css`
                                margin-bottom: 0;`
                            }>
                                <ExternalLink href="https://computop.com/agb">
                                    GTC / AGB
                                </ExternalLink>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

GlobalFooter.propTypes = {
    className: PropTypes.string,
};

export default GlobalFooter;
